/**
 * Single manifest for bundled template fonts under `public/assets/fonts/{catalog_name}/`.
 * Each family uses `{catalog_name}.{ttf|otf}`, `-italic`, `-bold`, `-bold-italic`.
 *
 * **Reuse scope:** Project-wide — template toolbar (`fontsList` order/labels), PDF registration, DOCX family names.
 * Next.js `localFont` paths are duplicated as string literals in `src/lib/fonts.ts` (compiler requirement).
 */

// MARK: BundledFontFormat
/** File extension for the regular face filename (`poppins.otf`, others `.ttf`). */
export type BundledFontFormat = "ttf" | "otf";

// MARK: BundledTemplateFontSpec
/** One bundled family row: `catalog_name` is the PDF `Font.register` id and stable template id; Lexical may store `next/font` `style.fontFamily` instead. */
export type BundledTemplateFontSpec = {
    catalog_name: string;
    /** Toolbar label and default Word `font` when that family is selected. */
    label: string;
    format: BundledFontFormat;
};

// MARK: BUNDLED_TEMPLATE_FONT_SPECS
/**
 * Order defines {@link fontsList} for bundled entries (before the system `segoe` row).
 *
 * **Reuse scope:** Project-wide — consumed by `src/lib/fonts.ts` and PDF/DOCX helpers.
 */
export const BUNDLED_TEMPLATE_FONT_SPECS: readonly BundledTemplateFontSpec[] = [
    { catalog_name: "courier_prime", label: "Courier", format: "ttf" },
    { catalog_name: "dm_sans", label: "DM Sans", format: "ttf" },
    { catalog_name: "inter", label: "Inter", format: "ttf" },
    { catalog_name: "lato", label: "Lato", format: "ttf" },
    { catalog_name: "libre_franklin", label: "Libre Franklin", format: "ttf" },
    { catalog_name: "lora", label: "Lora", format: "ttf" },
    { catalog_name: "miranda_sans", label: "Miranda Sans", format: "ttf" },
    { catalog_name: "montserrat", label: "Montserrat", format: "ttf" },
    { catalog_name: "open_sans", label: "Open Sans", format: "ttf" },
    { catalog_name: "playfair_display", label: "Playfair Display", format: "ttf" },
    { catalog_name: "poppins", label: "Poppins", format: "otf" },
    { catalog_name: "raleway", label: "Raleway", format: "ttf" },
    { catalog_name: "roboto", label: "Roboto", format: "ttf" },
    { catalog_name: "roboto_condensed", label: "Roboto Condensed", format: "ttf" },
    { catalog_name: "roboto_serif", label: "Roboto Serif", format: "ttf" },
    { catalog_name: "rubik", label: "Rubik", format: "ttf" },
    { catalog_name: "source_sans_pro", label: "Source Sans Pro", format: "otf" },
    { catalog_name: "times", label: "Times", format: "ttf" },
    { catalog_name: "ubuntu", label: "Ubuntu", format: "ttf" },
];

// MARK: bundled_template_font_specs_by_name
/** Fast lookup by `catalog_name` (throws if manifest drifts). */
export const bundled_template_font_specs_by_name: Readonly<Record<string, BundledTemplateFontSpec>> =
    Object.fromEntries(BUNDLED_TEMPLATE_FONT_SPECS.map((spec) => [spec.catalog_name, spec])) as Readonly<
        Record<string, BundledTemplateFontSpec>
    >;

// MARK: is_bundled_template_font_catalog_name
/** True if `name` is a bundled folder id (not `segoe`). */
export function is_bundled_template_font_catalog_name(name: string): boolean {
    return bundled_template_font_specs_by_name[name] != null;
}

// MARK: bundled_font_face_absolute_urls
/**
 * Absolute font file URLs for `@react-pdf/renderer` `Font.register` (fetch from `public/`).
 *
 * @param public_base - e.g. `https://app.example/` from {@link get_pdf_public_base_url}.
 */
export function bundled_font_face_absolute_urls(public_base: string, spec: BundledTemplateFontSpec) {
    const trimmed = public_base.trim().replace(/\/+$/, "");
    const ext = spec.format;
    const folder = spec.catalog_name;
    const stem = folder;
    const root = `${trimmed}/assets/fonts/${folder}/`;
    return {
        normal: `${root}${stem}.${ext}`,
        italic: `${root}${stem}-italic.${ext}`,
        bold: `${root}${stem}-bold.${ext}`,
        bold_italic: `${root}${stem}-bold-italic.${ext}`,
    };
}

// MARK: template_font_docx_family_name
/**
 * OOXML `w:rFonts` / `docx` `font` string for a template catalog id (`segoe` → Segoe UI).
 *
 * **Reuse scope:** Project-wide — DOCX export.
 */
export function template_font_docx_family_name(catalog_name: string): string {
    if (catalog_name === "segoe") {
        return "Segoe UI";
    }
    return bundled_template_font_specs_by_name[catalog_name]?.label ?? "Segoe UI";
}
